// ─────────────────────────────────────────────────────────────────────────────
// «Exportar ganadores» — UN solo Excel para el módulo Ganadores y para Brillamos Juntos.
//
// Sesión 2026-10-02:
//   · BJS-8 — el botón de Brillamos Juntos descarga EL MISMO archivo que «Exportar ganadores» del
//     módulo Ganadores (antes bajaba otro, y vacío en campañas activas). Por eso vive aquí y los dos
//     lo llaman, cada uno con sus filtros (Brillamos le pasa la campaña elegida).
//   · BJS-9 — con el diseño de la plantilla compartida (tema teal, título y encabezado como los demás
//     reportes) y con Site y País. La columna «Departamento» se cambia por el Site del padrón (`site`,
//     resuelto en SurveyService); la tabla de la pantalla sigue mostrando el departamento. El
//     Fundamental sale con su NOMBRE, no con su id.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  winnersApi, useWinnerBadges, useRecognitionCatalog, getBadgeName, getFundamentalName,
} from '@/api/recognition';
import type { WinnerRowDto, WinnerSearchParams } from '@/api/recognition';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellValue, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import {
  type Traductor, TEMA_PLANTILLA_TEAL, FILA_HEADER_TABLA_SIMPLE, SIN_DATO, estilosDeBanner, columnaDeTexto, columnaCentrada,
  rejillaDeTabla,
} from '../analytics/tabs/excelPlantillaTeal';

/** Lote del bucle: el máximo que acepta el backend por página (`WinnerSearchFilter.MaxPageSize`). */
const LOTE = 200;

export interface ResolutoresDeGanador {
  nombreInsignia: (badgeId: string | null | undefined) => string | null;
  anioGanador: (ganador: WinnerRowDto) => number | null;
  nombreDeFundamental: (fundamentalId: string) => string;
}

/**
 * Los mismos resolutores que pinta la tabla de Ganadores (insignia por id o código, año de la
 * insignia cuando la fila no lo trae, nombre del Fundamental en el idioma activo), para quien exporta
 * desde fuera del módulo (Brillamos Juntos). Ver `anioGanador` en `AdminWinners.tsx`.
 */
export function useResolutoresDeGanador(): ResolutoresDeGanador {
  const { i18n } = useTranslation('recognition');
  const badges = useWinnerBadges();
  const catalogo = useRecognitionCatalog();
  const insigniaDe = useCallback(
    (id: string | null | undefined) => (badges.data ?? []).find((b) => b.id === id || b.code === id),
    [badges.data],
  );
  const fundamentalPorId = useMemo(
    () => new Map((catalogo.data ?? []).map((f) => [f.id, f])),
    [catalogo.data],
  );
  return useMemo<ResolutoresDeGanador>(() => ({
    nombreInsignia: (id) => {
      if (!id) return null;
      const b = insigniaDe(id);
      return b ? getBadgeName(b, i18n.language) : id;
    },
    anioGanador: (w) => w.anio ?? (w.badgeId ? insigniaDe(w.badgeId)?.anio ?? null : null),
    nombreDeFundamental: (id) => {
      const f = fundamentalPorId.get(id);
      return f ? getFundamentalName(f, i18n.language) : id;
    },
  }), [insigniaDe, fundamentalPorId, i18n.language]);
}

/** Salvaguarda contra un recorrido que no termina (ver `MAX_PAGINAS` en `fetchNominacionesCrudas.ts`). */
const MAX_PAGINAS_GANADORES = 50_000;

/** Trae TODAS las filas del filtro, página por página (sin tope desde el 2026-10-19; antes 5000).
 * `page`/`pageSize` del filtro se ignoran: el recorrido pide sus propios lotes. */
export async function traerGanadores(filtros: WinnerSearchParams): Promise<WinnerRowDto[]> {
  const filas: WinnerRowDto[] = [];
  let pagina = 1;
  let total = 0;
  do {
    if (pagina > MAX_PAGINAS_GANADORES) {
      throw new Error(`El export de ganadores superó ${MAX_PAGINAS_GANADORES} páginas sin terminar.`);
    }
    const res = await winnersApi.search({ ...filtros, page: pagina, pageSize: LOTE });
    // 🔴 Una página VACÍA corta el bucle aunque el total diga que faltan filas: si los datos cambian
    // entre dos peticiones, la condición de avance dejaría de avanzar y el bucle no terminaría.
    if (res.filas.length === 0) break;
    filas.push(...res.filas);
    total = res.total;
    pagina += 1;
  } while (filas.length < total);
  return filas;
}

/** La hoja de ganadores con el diseño de la plantilla compartida. Pura, para poder probarla. */
export function buildGanadoresSheet(
  ganadores: WinnerRowDto[], t: Traductor, r: ResolutoresDeGanador,
): ExcelSheetSpec {
  const columnas = [
    t('admin.winners.colUser'), t('admin.winners.colEmail'), t('admin.winners.colPuesto'),
    t('admin.winners.colOu'), t('admin.winners.colSite'), t('admin.winners.colCountry'),
    t('admin.winners.colFundamental'), t('admin.winners.colYear'), t('admin.winners.colBadge'),
    t('admin.winners.colStatus'),
  ];
  const banner = estilosDeBanner(columnas.length);
  const primeraFilaDato = FILA_HEADER_TABLA_SIMPLE + 1;
  const ultimaFilaDato = primeraFilaDato + ganadores.length - 1;

  return {
    name: t('admin.winners.exportSheetName'),
    rows: [
      [t('admin.winners.exportTitle')],
      [t('admin.winners.exportSubtitle')],
      [],
      columnas,
      ...ganadores.map((w): ExcelCellValue[] => [
        w.nombre,
        w.email ?? SIN_DATO,
        w.area ?? SIN_DATO,
        w.ouName ?? w.ouId ?? SIN_DATO,
        w.site ?? SIN_DATO,
        w.country ?? SIN_DATO,
        w.fundamentalId ? r.nombreDeFundamental(w.fundamentalId) : SIN_DATO,
        r.anioGanador(w) ?? SIN_DATO,
        r.nombreInsignia(w.badgeId) ?? SIN_DATO,
        w.estado === 'Confirmado' ? t('admin.winners.statusConfirmed') : t('admin.winners.statusPending'),
      ]),
    ],
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_TABLA_SIMPLE],
      columnWidths: [30, 32, 26, 16, 26, 16, 26, 10, 26, 14],
      rowHeights: banner.rowHeights,
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [
          { ...columnaDeTexto(0), isBold: true },
          columnaDeTexto(1), columnaDeTexto(2), columnaCentrada(3), columnaDeTexto(4), columnaCentrada(5),
          columnaDeTexto(6), columnaCentrada(7), columnaDeTexto(8), columnaCentrada(9),
        ],
      },
      cellStyles: banner.cellStyles,
      borderedRanges: [rejillaDeTabla(FILA_HEADER_TABLA_SIMPLE, Math.max(ultimaFilaDato, FILA_HEADER_TABLA_SIMPLE), columnas.length - 1)],
    },
  };
}

/**
 * Descarga el Excel de ganadores del filtro, completo. Devuelve cuántas filas salieron; con 0 filas NO
 * descarga (un libro con solo encabezados parece un fallo sin serlo) y quien llama avisa.
 */
export async function exportarGanadoresAExcel(opciones: {
  filtros: WinnerSearchParams;
  t: Traductor;
  resolutores: ResolutoresDeGanador;
  nombreArchivo: string;
  /**
   * 2026-10-14 (BJT-1) — «Todas las campañas» de Brillamos Juntos: los ganadores de cada campaña de la
   * lista, en un mismo archivo (la búsqueda acepta una sola campaña).
   */
  campanas?: readonly string[];
}): Promise<{ filas: number }> {
  // BJT-1 — con «Todas las campañas», una búsqueda por campaña de la lista, unidas.
  const busquedas = opciones.campanas?.length
    ? opciones.campanas.map((campaignId) => ({ ...opciones.filtros, campaignId }))
    : [opciones.filtros];
  const filas: WinnerRowDto[] = [];
  for (const filtros of busquedas) {
    filas.push(...await traerGanadores(filtros));
  }
  if (filas.length === 0) return { filas: 0 };
  await exportMultiSheetExcel({
    fileName: opciones.nombreArchivo,
    sheets: [buildGanadoresSheet(filas, opciones.t, opciones.resolutores)],
  });
  return { filas: filas.length };
}
